import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useId,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BdsChipClickable, BdsIcon } from '@/core/constants/webComponents';
import { useDropdownKeyboardNav } from '@/core/utils/useDropdownKeyboardNav';
import { normalizeString as normalize } from '@/core/utils/normalizeString';
import { ItemSelectorProps, ItemState } from './types';
import './ItemSelector.scss';

const SELECT_ALL_KEY = '__select_all__';

export const ItemSelector: React.FC<ItemSelectorProps> = ({
  label,
  placeholder,
  selectAllLabel,
  availableItems,
  availableLoading = false,
  selection,
  onChange,
  canCreate = false,
  onCreateItem,
  onCreateItems,
}) => {
  const { t } = useTranslation();
  const labelId = useId();
  const listId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pasteNotFound, setPasteNotFound] = useState<string[]>([]);
  const [pendingPasteCreates, setPendingPasteCreates] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const chipRefs = useRef<(HTMLElement | null)[]>([]);
  const chipWrapperRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const customListRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLSpanElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const chipsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      if (target.closest('.me-selector__arrow') || target.closest('button')) {
        return;
      }
      setIsOpen(true);
      requestAnimationFrame(() => {
        searchRef.current?.focus();
      });
    };
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    const el = arrowRef.current;
    if (!el) return;
    const handleClick = (e: MouseEvent) => {
      e.stopPropagation();
      setIsOpen((o) => !o);
    };
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    } else if (chipsContainerRef.current) {
      chipsContainerRef.current.scrollTop = 0;
      chipsContainerRef.current.scrollLeft = 0;
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const labelElement = containerRef.current?.querySelector('.me-selector__label');
      const isClickOnLabel = labelElement && (labelElement === target || labelElement.contains(target));

      if (isOpen && containerRef.current && (!containerRef.current.contains(target) || isClickOnLabel)) {
        setIsOpen(false);
        setSearch('');
        setPendingPasteCreates([]);
        setPasteNotFound([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const sortedAvailableItems = useMemo(() => {
    return [...availableItems].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [availableItems]);

  const { handleKeyDown: handleListKeyDown, handleItemFocus } = useDropdownKeyboardNav(
    isOpen,
    customListRef,
    () => { setIsOpen(false); setSearch(''); },
    '.me-selector__option',
    false,
  );

  const filtered = useMemo(() => {
    const q = normalize(search).replaceAll(/\s+/g, ' ').replaceAll(/-+/g, ' ').trim();
    if (!q) return sortedAvailableItems;

    return sortedAvailableItems.filter((item) => normalize(item).replaceAll(/[-\s]+/g, ' ').includes(q));
  }, [sortedAvailableItems, search]);

  const isFiltered = search.trim().length > 0;
  const currentSelectAllLabel = isFiltered ? t('multipleEdition.selectAllFiltered') : selectAllLabel;
  const dropdownTargetItems = isFiltered ? filtered : sortedAvailableItems;

  const allAddGlobal = sortedAvailableItems.length > 0 && sortedAvailableItems.every((i) => selection[i] === 'add');
  const allRemoveGlobal = sortedAvailableItems.length > 0 && sortedAvailableItems.every((i) => selection[i] === 'remove');

  const dropdownAllAdd = dropdownTargetItems.length > 0 && dropdownTargetItems.every((i) => selection[i] === 'add');
  const dropdownAllRemove = dropdownTargetItems.length > 0 && dropdownTargetItems.every((i) => selection[i] === 'remove');

  let selectAllPressedState: true | false | 'mixed' = false;
  if (dropdownAllAdd) selectAllPressedState = true;
  else if (dropdownAllRemove) selectAllPressedState = 'mixed';

  const handleToggleItem = useCallback((item: string) => {
    setSearch('');
    handleItemFocus(-1);
    onChange((prev) => {
      const current = prev[item] ?? null;
      let next: ItemState | null;
      if (current === null) {
        next = 'add';
      } else if (current === 'add') {
        next = 'remove';
      } else {
        next = null;
      }
      const updated = { ...prev };
      if (next === null) {
        delete updated[item];
      } else {
        updated[item] = next;
      }
      return updated;
    });
  }, [onChange, handleItemFocus]);

  const handleRemoveItem = useCallback((item: string) => {
    onChange((prev) => {
      const updated = { ...prev };
      delete updated[item];
      return updated;
    });
  }, [onChange]);

  const handleToggleGlobalAll = useCallback(() => {
    setSearch('');
    handleItemFocus(-1);
    onChange((prev) => {
      const isAllAdd = sortedAvailableItems.length > 0 && sortedAvailableItems.every((i) => prev[i] === 'add');
      const isAllRemove = sortedAvailableItems.length > 0 && sortedAvailableItems.every((i) => prev[i] === 'remove');
      const updated = { ...prev };
      if (isAllAdd) {
        sortedAvailableItems.forEach((i) => {
          updated[i] = 'remove';
        });
      } else if (isAllRemove) {
        sortedAvailableItems.forEach((i) => delete updated[i]);
      } else {
        sortedAvailableItems.forEach((i) => {
          updated[i] = 'add';
        });
      }
      return updated;
    });
  }, [onChange, sortedAvailableItems, handleItemFocus]);

  const handleToggleDropdownAll = useCallback(() => {
    setSearch('');
    handleItemFocus(-1);
    onChange((prev) => {
      const isTargetAllAdd = dropdownTargetItems.length > 0 && dropdownTargetItems.every((i) => prev[i] === 'add');
      const isTargetAllRemove = dropdownTargetItems.length > 0 && dropdownTargetItems.every((i) => prev[i] === 'remove');
      const updated = { ...prev };
      if (isTargetAllAdd) {
        dropdownTargetItems.forEach((i) => {
          updated[i] = 'remove';
        });
      } else if (isTargetAllRemove) {
        dropdownTargetItems.forEach((i) => delete updated[i]);
      } else {
        dropdownTargetItems.forEach((i) => {
          updated[i] = 'add';
        });
      }
      return updated;
    });
  }, [onChange, dropdownTargetItems, handleItemFocus]);

  const selectionEntries = useMemo(() => Object.entries(selection), [selection]);

  // Scroll to search input whenever a new chip is added or search changed
  useEffect(() => {
    let frameId: number;
    if (searchRef.current) {
      frameId = requestAnimationFrame(() => {
        searchRef.current?.scrollIntoView({ block: 'nearest', inline: 'start' });
      });
    }
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [selectionEntries.length, search]);

  useEffect(() => {
    const cleanups: (() => void)[] = [];
    if (allAddGlobal || allRemoveGlobal) {
      const el = chipRefs.current[0];
      if (el) {
        const handler = (e: Event) => { e.stopPropagation(); handleToggleGlobalAll(); };
        el.addEventListener('chipClickableClose', handler);
        cleanups.push(() => el.removeEventListener('chipClickableClose', handler));
      }
    } else {
      selectionEntries.forEach(([key], index) => {
        const el = chipRefs.current[index];
        if (!el) return;
        const handler = (e: Event) => {
          e.stopPropagation();
          handleRemoveItem(key);
        };
        el.addEventListener('chipClickableClose', handler);
        cleanups.push(() => el.removeEventListener('chipClickableClose', handler));
      });
    }
    return () => cleanups.forEach((fn) => fn());
  }, [selectionEntries, handleRemoveItem, handleToggleItem, allAddGlobal, allRemoveGlobal, handleToggleGlobalAll]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) {
        if (filtered.length > 0) handleToggleItem(filtered[0]);
      } else {
        setIsOpen(true);
      }
    }
    if (e.key === ' ' && search === '' && !isOpen) {
      // Space key toggles dropdown when search is empty and dropdown is closed
      e.preventDefault();
      setIsOpen(true);
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
      setPasteNotFound([]);
      setPendingPasteCreates([]);
    }
    // ArrowLeft at start of input → focus last chip
    if (e.key === 'ArrowLeft' && searchRef.current?.selectionStart === 0) {
      const wrappers = chipWrapperRefs.current.filter(Boolean);
      if (wrappers.length > 0) {
        e.preventDefault();
        wrappers.at(-1)?.focus();
      }
    }
  };

  const focusChip = (index: number) => {
    const wrappers = chipWrapperRefs.current.filter(Boolean);
    const target = wrappers[index];
    if (target) {
      target.focus();
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  };

  const handleChipKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>, key: string, index: number, isAll: boolean) => {
    const wrappers = chipWrapperRefs.current.filter(Boolean);
    const lastIndex = wrappers.length - 1;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (index > 0) focusChip(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (index < lastIndex) {
        focusChip(index + 1);
      } else {
        searchRef.current?.focus();
      }
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      if (isAll) {
        handleToggleGlobalAll();
      } else {
        handleRemoveItem(key);
        // Move focus to previous chip or input after removal
        requestAnimationFrame(() => {
          const updated = chipWrapperRefs.current.filter(Boolean);
          if (updated.length > 0) {
            const next = updated[Math.min(index, updated.length - 1)];
            next?.focus();
          } else {
            searchRef.current?.focus();
          }
        });
      }
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Printable character → redirect to search input
      searchRef.current?.focus();
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPasteNotFound([]);
    if (!isOpen) setIsOpen(true);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const lines = text.split(/[\r\n,;]+/).map((l) => l.trim()).filter(Boolean);
    if (lines.length <= 1) return; // single item: let default paste handle it
    e.preventDefault();

    const toAdd: string[] = [];
    const toRemove: string[] = [];
    const notFound: string[] = [];

    lines.forEach((line) => {
      const isRemove = line.startsWith('-');
      const name = isRemove ? line.slice(1).trim() : line;
      const match = sortedAvailableItems.find((i) => normalize(i) === normalize(name));
      if (match) {
        if (isRemove) {
          toRemove.push(match);
        } else {
          toAdd.push(match);
        }
      } else {
        notFound.push(name);
      }
    });

    if (toAdd.length > 0 || toRemove.length > 0) {
      onChange((prev) => {
        const updated = { ...prev };
        toAdd.forEach((item) => { updated[item] = 'add'; });
        toRemove.forEach((item) => { updated[item] = 'remove'; });
        return updated;
      });
    }

    if (notFound.length > 0) {
      if (canCreate) {
        setPendingPasteCreates(notFound);
        setIsOpen(true);
      } else {
        setPasteNotFound(notFound);
      }
    }
  };

  return (
    <div className="me-selector" ref={containerRef}>
      <span className="me-selector__label" id={labelId}>{label}</span>
      <div
        ref={fieldRef}
        className={`me-selector__field${isOpen ? ' me-selector__field--open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-labelledby={labelId}
      >
        <span className="me-selector__trigger-content">
          <span className="me-selector__chips" ref={chipsContainerRef}>
            {(allAddGlobal || allRemoveGlobal) ? (
              <button
                type="button"
                ref={(el) => { chipWrapperRefs.current[0] = el; }}
                className="me-selector__chip-wrapper"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => handleChipKeyDown(e, SELECT_ALL_KEY, 0, true)}
                aria-label={selectAllLabel}
              >
                <BdsChipClickable
                  ref={(el: HTMLElement | null) => { chipRefs.current[0] = el; }}
                  color="outline"
                  close
                >
                  <span className="me-selector__chip-content">
                    {allRemoveGlobal && (
                      <svg viewBox="0 0 16 16" aria-hidden="true" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.99967 1.33301C9.78034 1.33301 11.4546 2.02647 12.7138 3.28562C13.9728 4.54478 14.6663 6.21897 14.6663 7.99967C14.6663 9.78034 13.9728 11.4546 12.7138 12.7138C11.4546 13.9728 9.78034 14.6663 7.99967 14.6663C6.21897 14.6663 4.54478 13.9728 3.28562 12.7138C2.02647 11.4546 1.33301 9.78034 1.33301 7.99967C1.33301 6.21897 2.02647 4.54478 3.28562 3.28562C4.54478 2.02647 6.21897 1.33301 7.99967 1.33301ZM7.99967 13.6247C11.1013 13.6247 13.6247 11.1013 13.6247 7.99967C13.6247 4.89807 11.1013 2.37467 7.99967 2.37467C4.89807 2.37467 2.37467 4.89807 2.37467 7.99967C2.37467 11.1013 4.89807 13.6247 7.99967 13.6247ZM5.13509 8.52102H10.8643V7.47935H5.13509V8.52102Z" fill="#E60F0F"/>
                      </svg>
                    )}
                    {selectAllLabel}
                  </span>
                </BdsChipClickable>
              </button>
            ) : (
              selectionEntries.map(([key, state], index) => (
                <button
                  key={key}
                  type="button"
                  ref={(el) => { chipWrapperRefs.current[index] = el; }}
                  className="me-selector__chip-wrapper"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => handleChipKeyDown(e, key, index, false)}
                  aria-label={key}
                >
                  <BdsChipClickable
                    ref={(el: HTMLElement | null) => { chipRefs.current[index] = el; }}
                    color="outline"
                    close
                  >
                    <span className="me-selector__chip-content">
                      {state === 'remove' && (
                        <svg viewBox="0 0 16 16" aria-hidden="true" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7.99967 1.33301C9.78034 1.33301 11.4546 2.02647 12.7138 3.28562C13.9728 4.54478 14.6663 6.21897 14.6663 7.99967C14.6663 9.78034 13.9728 11.4546 12.7138 12.7138C11.4546 13.9728 9.78034 14.6663 7.99967 14.6663C6.21897 14.6663 4.54478 13.9728 3.28562 12.7138C2.02647 11.4546 1.33301 9.78034 1.33301 7.99967C1.33301 6.21897 2.02647 4.54478 3.28562 3.28562C4.54478 2.02647 6.21897 1.33301 7.99967 1.33301ZM7.99967 13.6247C11.1013 13.6247 13.6247 11.1013 13.6247 7.99967C13.6247 4.89807 11.1013 2.37467 7.99967 2.37467C4.89807 2.37467 2.37467 4.89807 2.37467 7.99967C2.37467 11.1013 4.89807 13.6247 7.99967 13.6247ZM5.13509 8.52102H10.8643V7.47935H5.13509V8.52102Z" fill="#E60F0F"/>
                        </svg>
                      )}
                      {key}
                    </span>
                  </BdsChipClickable>
                </button>
              ))
            )}
            <input
              ref={searchRef}
              className="me-selector__search-input"
              aria-labelledby={labelId}
              value={search}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                handleTriggerKeyDown(e);
                handleListKeyDown(e);
              }}
              onPaste={handlePaste}
              placeholder={selectionEntries.length === 0 ? placeholder : ''}
              onClick={() => {
                // If dropdown is not open and clicking on input, toggle it
                if (!isOpen) {
                  setIsOpen(true);
                }
                // Do not stop propagation so the container's onClick can also fire or we can just ensure focus:
                requestAnimationFrame(() => {
                  searchRef.current?.focus();
                });
              }}
            />
          </span>
        </span>
        <span ref={arrowRef} className={`me-selector__arrow${isOpen ? ' me-selector__arrow--open' : ''}`} aria-hidden="true">
          <BdsIcon name="arrow-down" size="x-small" />
        </span>
      </div>
      {pasteNotFound.length > 0 && (
        <span className="me-selector__paste-warning">
          {t('multipleEdition.pasteNotFound', { items: pasteNotFound.join(', ') })}
        </span>
      )}
      {isOpen && (
        <div className="me-selector__dropdown">
          {/* Hidden native select for accessibility/semantics (not exposed to assistive tech). */}
          <select
            ref={selectRef}
            multiple
            tabIndex={-1}
            aria-hidden="true"
            style={{ display: 'none' }}
            value={selectionEntries.filter(([, state]) => state === 'add').map(([k]) => k)}
            onChange={() => {}}
          >
            {sortedAvailableItems.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          {availableLoading ? (
            <div className="me-selector__loading">
              <span className="me-selector__spinner" aria-hidden="true" />
              <span className="me-selector__loading-text">{t('multipleEdition.loading')}</span>
            </div>
          ) : (
            <>
              {pendingPasteCreates.length > 0 && (
                <div className="me-selector__option--paste-section">
                  <span className="me-selector__paste-header-label">
                    {t('multipleEdition.pasteCreatePrompt')}
                  </span>
                  <button
                    type="button"
                    className="me-selector__option-btn"
                    onKeyDown={handleListKeyDown}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateItems?.(pendingPasteCreates);
                      setPendingPasteCreates([]);
                      requestAnimationFrame(() => {
                        searchRef.current?.focus();
                        searchRef.current?.scrollIntoView({ block: 'nearest', inline: 'start' });
                      });
                    }}
                  >
                    <BdsIcon name="add" size="x-small" color="#fff" />
                    <span className="me-selector__option-text">
                      {t('multipleEdition.createItems', { names: pendingPasteCreates.join(', ') })}
                    </span>
                  </button>
                </div>
              )}
              <div
                id={listId}
                className="me-selector__list"
                ref={customListRef}
              >
                <button
                  type="button"
                  className="me-selector__option"
                  data-testid="option-select-all"
                  aria-pressed={selectAllPressedState}
                  onClick={() => {
                    handleToggleDropdownAll();
                    requestAnimationFrame(() => {
                      searchRef.current?.focus();
                      searchRef.current?.scrollIntoView({ block: 'nearest', inline: 'start' });
                    });
                  }}
                  onFocus={() => handleItemFocus(0)}
                  onKeyDown={(e) => {
                    handleListKeyDown(e);
                    // Enter is already handled by handleListKeyDown (simulated click on the
                    // focused item) — only Space needs explicit handling here, otherwise
                    // Enter would toggle the item twice (add → remove) in a single press.
                    if (e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleToggleDropdownAll();
                    }
                  }}
                >
                  {(() => {
                      let allCheckboxMod = '';
                      if (dropdownAllAdd) allCheckboxMod = ' me-selector__checkbox--add';
                      else if (dropdownAllRemove) allCheckboxMod = ' me-selector__checkbox--remove';
                      return (
                        <div className={`me-selector__checkbox${allCheckboxMod}`}>
                          {dropdownAllAdd && <span className="me-selector__check-icon">&#10003;</span>}
                          {dropdownAllRemove && <span className="me-selector__check-icon">&#8722;</span>}
                        </div>
                      );
                    })()}
                    <span className="me-selector__option-text">{currentSelectAllLabel}</span>
                  </button>
                {filtered.map((item, index) => {
                  const state = selection[item] ?? null;
                  let itemPressedState: true | false | 'mixed' = false;
                  if (state === 'add') itemPressedState = true;
                  else if (state === 'remove') itemPressedState = 'mixed';
                  return (
                    <button
                      key={item}
                      type="button"
                      className="me-selector__option"
                      data-testid={`option-${item}`}
                      aria-pressed={itemPressedState}
                      onClick={() => {
                        handleToggleItem(item);
                        requestAnimationFrame(() => {
                          searchRef.current?.focus();
                          searchRef.current?.scrollIntoView({ block: 'nearest', inline: 'start' });
                        });
                      }}
                      onFocus={() => handleItemFocus(index + 1)}
                      onKeyDown={(e) => {
                        handleListKeyDown(e);
                        // Same reasoning as the select-all button above: Enter already
                        // triggers a click via handleListKeyDown, so only handle Space here.
                        if (e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleItem(item);
                        }
                      }}
                      >
                        {(() => {
                          let checkboxMod = '';
                          if (state === 'add') checkboxMod = ' me-selector__checkbox--add';
                          else if (state === 'remove') checkboxMod = ' me-selector__checkbox--remove';
                          return (
                            <div className={`me-selector__checkbox${checkboxMod}`}>
                              {state === 'add' && <span className="me-selector__check-icon">&#10003;</span>}
                              {state === 'remove' && <span className="me-selector__check-icon">&#8722;</span>}
                            </div>
                          );
                        })()}
                        <span className="me-selector__option-text">{item}</span>
                      </button>
                  );
                })}
                {canCreate && search.trim() !== '' && !sortedAvailableItems.some((i) => i.toLowerCase() === search.trim().toLowerCase()) && (
                  <button
                    type="button"
                    className="me-selector__option--create"
                    data-testid={`option-create-${search.trim()}`}
                    onFocus={() => handleItemFocus(filtered.length + 1)}
                    onKeyDown={(e) => {
                      handleListKeyDown(e);
                      // Same reasoning: Enter is already handled by handleListKeyDown's
                      // simulated click, so only handle Space explicitly here.
                      if (e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        const name = search.trim();
                        onCreateItem?.(name);
                        setSearch('');
                        handleItemFocus(-1);
                        requestAnimationFrame(() => {
                          searchRef.current?.focus();
                          searchRef.current?.scrollIntoView({ block: 'nearest', inline: 'start' });
                        });
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const name = search.trim();
                      onCreateItem?.(name);
                      setSearch('');
                      handleItemFocus(-1);
                      requestAnimationFrame(() => {
                        searchRef.current?.focus();
                        searchRef.current?.scrollIntoView({ block: 'nearest', inline: 'start' });
                      });
                    }}
                    >
                      <BdsIcon name="add" size="x-small" color="#fff" />
                      <span className="me-selector__option-text">
                        {t('multipleEdition.createItem', { name: search.trim() })}
                      </span>
                    </button>
                )}  
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
